'use client';

import { Container, Carousel } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import BlogComponent from '../blogComponent/blogComponent';
import './masterSuite.scss';
import { GetBlogSettingV2Result } from '@/sanity.types';
import { imageData } from '@/config/defaultBlur';

interface MasterSuitProps {
  data: NonNullable<GetBlogSettingV2Result>;
}
const MasterSuite = ({ data }: MasterSuitProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(4);
  const [isMobile, setIsMobile] = useState(false);

  // Update items per slide and mobile state based on screen size
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      setIsMobile(mobile);

      if (width < 479) {
        setItemsPerSlide(1); // XS Mobile
      } else if (width < 767) {
        setItemsPerSlide(2); // Mobile
      } else if (width < 991) {
        setItemsPerSlide(3); // Tablet
      } else {
        setItemsPerSlide(4); // Desktop
      }
    };

    // Set initial value
    updateScreenSize();

    // Add event listener
    window.addEventListener('resize', updateScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Get dynamic data from data.sugSuite.cards or fallback to empty array
  const masterPosts =
    data?.sugSuite?.cards?.map((card, index) => ({
      id: (index + 1).toString(),
      title: card?.title || '',
      href: card?.link || '#',
      image: card?.image,
      alt: card?.image?.alt || card?.title || 'Blog image',
      blurDataURL: card?.image?.imgAsset?.metadata?.lqip || imageData.blurImage,
    })) || [];

  // Determine if we should show carousel based on screen size and card count
  // Desktop: carousel if 5+ cards, otherwise static grid
  // Mobile: carousel if > 1 card
  const showCarousel = isMobile
    ? masterPosts.length > 1
    : masterPosts.length >= 5;

  // Group posts into slides based on itemsPerSlide (only if showing carousel)
  const slides = [];
  if (showCarousel) {
    // First, create slides with original data
    for (let i = 0; i < masterPosts.length; i += itemsPerSlide) {
      slides.push(masterPosts.slice(i, i + itemsPerSlide));
    }

    // If the last slide is incomplete, fill it with posts from the beginning to make it infinite
    const lastSlide = slides[slides.length - 1];
    if (lastSlide && lastSlide.length < itemsPerSlide) {
      let fillIndex = 0;
      while (lastSlide.length < itemsPerSlide) {
        lastSlide.push(masterPosts[fillIndex % masterPosts.length]);
        fillIndex++;
      }
    }
  }

  // Reset active index when items per slide changes or mobile state changes
  useEffect(() => {
    setActiveIndex(0);
  }, [itemsPerSlide, isMobile]);

  const handleSelect = (selectedIndex: number) => {
    setActiveIndex(selectedIndex);
  };

  // Return null if no data or no cards available
  if (!data?.sugSuite?.cards || data.sugSuite.cards.length === 0) {
    return null;
  }

  return (
    <div className="masterSuiteWrap">
      <Container>
        <div className="badge">
          <span>Master the SignUpGenius Suite</span>
        </div>
        {showCarousel ? (
          <>
            <Carousel
              activeIndex={activeIndex}
              onSelect={handleSelect}
              controls={false}
              className="masterSuiteCarousel"
              indicators={true}
              interval={3000}
              slide={true}
              fade={false}
              pause="hover"
            >
              {slides.map((slide, slideIndex) => (
                <Carousel.Item key={slideIndex}>
                  <div className="masterSuiteContent carousel-content">
                    {slide.map((post) => (
                      <BlogComponent
                        key={post.id}
                        /* @ts-expect-error - data is not properly typed, but we know it exists */
                        src={post.image || '/n_images/default-blog.jpg'}
                        alt={post.alt || ''}
                        blogTitle={post.title || ''}
                        href={post.href || ''}
                        blurDataURL={post.blurDataURL || imageData.blurImage}
                      />
                    ))}
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </>
        ) : (
          /* Static column layout for < 5 cards */
          <div className="masterSuiteContent">
            {masterPosts.map((post) => (
              <BlogComponent
                key={post.id}
                /* @ts-expect-error - data is not properly typed, but we know it exists */
                src={post.image || '/n_images/default-blog.jpg'}
                alt={post.alt || ''}
                blogTitle={post.title || ''}
                href={post.href || ''}
                blurDataURL={post.blurDataURL || imageData.blurImage}
              />
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};

export default MasterSuite;
